import { FloatType, HalfFloatType, TypedArray, WebGLRenderer, WebGLRenderTarget } from "three";
import { ILogService } from "../../log/common/log";

/**
 * Shared render-target → image readback for editor-initiated renders
 * (texture conversion, resource previews). Extracted from TextureConverter so
 * every preview path inherits the same WebGL/WebGPU handling — in particular
 * the WebGPU queue-kick workaround for on-demand rendering.
 */

export interface ReadbackOptions {
    /**
     * Apply a linear→sRGB transfer per pixel. three applies tone mapping and
     * output color-space encoding only to the default framebuffer — content
     * rendered to a render target reads back linear (visibly too dark for lit
     * previews). Leave off for unlit blits of already-encoded textures.
     */
    encodeSRGB?: boolean;
    /** Convert premultiplied scene pixels to straight alpha before color encoding. */
    premultipliedAlpha?: boolean;
    /**
     * "data:url" (default, canvas.toDataURL — the TextureConverter legacy
     * behavior) or "blob:url" (canvas.toBlob + URL.createObjectURL — smaller
     * memory, revocable; caller owns revocation).
     */
    output?: "data:url" | "blob:url";
    /**
     * Downscale so the longest edge is at most this many pixels (aspect kept)
     * before encoding — for thumbnails, where a full-resolution data URL of a
     * 2K render target is pure memory waste. Omit for full resolution.
     */
    maxSize?: number;
    /**
     * Force every pixel opaque (alpha 255) — the RenderDoc convention for
     * intermediate render targets, which often carry their content in RGB
     * with alpha left at 0 (bloom/additive chains never read it); an
     * alpha-respecting display would show nothing.
     *
     * `"ifAlphaEmpty"` only lifts alpha when the channel is effectively unused
     * (all ~0, the bloom/additive case) and otherwise PRESERVES real
     * transparency — so a pass output that genuinely uses alpha reads back with
     * see-through pixels instead of solid black. `true` always lifts.
     */
    forceOpaque?: boolean | "ifAlphaEmpty";
    /**
     * Row order handling. `"auto"` (default) flips GL readbacks and trusts
     * WebGPU-backend buffers top-down — right for converted/blitted textures.
     * `"bottom-up"` treats the buffer as bottom-up on EVERY backend: three
     * renders into render targets with GL conventions even on WebGPU (the
     * projection is flipped so RT textures sample identically across
     * backends), so scene-rendered pass targets always need the flip.
     */
    rows?: "auto" | "bottom-up";
}

// one shared scratch canvas — all readbacks are serialized on the GPU work
// queue, and toBlob/toDataURL snapshot the bitmap synchronously at call time
let _canvas: HTMLCanvasElement | null = null;

/**
 * Reads the pixels of a render target back into a canvas and encodes it as a
 * data: or blob: URL. Handles classic WebGL (async readback where available)
 * and three r182+ WebGPU (`readRenderTargetPixelsAsync` + an empty
 * `queue.submit([])` nudge — with on-demand rendering nothing may tick the GPU
 * queue while idle, leaving the readback pending until unrelated work submits
 * a frame).
 */
export async function readbackRenderTargetToUrl(
    renderTarget: WebGLRenderTarget,
    renderer: WebGLRenderer,
    logger: ILogService | null,
    options?: ReadbackOptions,
): Promise<string | null> {
    const width = renderTarget.width;
    const height = renderTarget.height;
    const pixels = await readbackRenderTargetToPixels(renderTarget, renderer, logger, options);
    if (!pixels) return null;

    if (options?.forceOpaque === true || (options?.forceOpaque === "ifAlphaEmpty" && alphaIsEffectivelyEmpty(pixels))) {
        for (let i = 3; i < pixels.length; i += 4) pixels[i] = 255;
    }

    let canvas = _canvas ??= document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);

    const maxSize = options?.maxSize;
    if (maxSize && Math.max(width, height) > maxSize) {
        const scale = maxSize / Math.max(width, height);
        const scaled = _scaleCanvas ??= document.createElement("canvas");
        scaled.width = Math.max(1, Math.round(width * scale));
        scaled.height = Math.max(1, Math.round(height * scale));
        const scaledCtx = scaled.getContext("2d");
        if (scaledCtx) {
            scaledCtx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
            canvas = scaled;
        }
    }

    if (options?.output === "blob:url") {
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/png"));
        return blob ? URL.createObjectURL(blob) : null;
    }
    return canvas.toDataURL();
}
let _scaleCanvas: HTMLCanvasElement | null = null;

/**
 * Heuristic: does this readback's alpha channel carry real transparency, or was
 * it never written (a bloom/additive/data pass leaves content in RGB with alpha
 * cleared to 0)? Returns true (= "empty", force it opaque) only when almost no
 * pixel carries alpha — so a buffer that genuinely uses alpha keeps its
 * see-through pixels. The 1% floor tolerates a few stray non-zero alphas in an
 * otherwise-unused channel without blanking a real bloom preview.
 *
 * Cheap: reads one byte per pixel and EARLY-EXITS once enough alpha is seen to
 * conclude "not empty" — so a normal (alpha-using) texture stops after ~1% of
 * pixels; only a truly-empty channel is scanned in full. Runs once per capture
 * (cached), dwarfed by the readback + sRGB pass + PNG encode on the same path.
 */
function alphaIsEffectivelyEmpty(pixels: Uint8Array | Uint8ClampedArray): boolean {
    const total = pixels.length >> 2;
    if (total === 0) return true;
    const need = total * 0.01; // this many alpha-carrying pixels ⇒ not empty
    let used = 0;
    for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] >= 8 && ++used >= need) return false;
    }
    return true;
}

/**
 * Raw variant for per-frame consumers (interactive preview sessions): returns
 * tightly packed, top-down, display-ready RGBA8 pixels — orientation and sRGB
 * handling included — without the expensive image encode.
 *
 * Pass `reuse` (a buffer sized exactly `width*height*4`) to avoid allocating a
 * fresh ~1 MB array every frame during an interactive orbit — the session owns
 * one buffer and reuses it (frames are serialized, so the previous frame's
 * pixels are fully consumed by putImageData before the next overwrites them).
 */
export async function readbackRenderTargetToPixels(
    renderTarget: WebGLRenderTarget,
    renderer: WebGLRenderer,
    logger: ILogService | null,
    options?: Pick<ReadbackOptions, "encodeSRGB" | "rows" | "premultipliedAlpha"> & {
        onTiming?: (timing: { readbackMs: number; conversionMs: number }) => void;
    },
    reuse?: Uint8Array,
): Promise<Uint8Array | null> {
    const width = renderTarget.width;
    const height = renderTarget.height;
    const size = width * height * 4;
    const pixels = reuse && reuse.length === size ? reuse : new Uint8Array(size);

    const started = options?.onTiming ? performance.now() : 0;
    const read = await readRenderTargetRaw(renderTarget, renderer, logger, pixels);
    const readFinished = options?.onTiming ? performance.now() : 0;
    if (!read) return null;
    // the 8-bit GL path reads STRAIGHT into `pixels` (no stride padding there) —
    // anything else needs the decode/unpad pass
    if (read.data !== pixels && !copyReadbackPixels(read.data, width, height, pixels)) {
        logger?.warn(`Readback: unexpected GPU readback buffer (${read.data?.constructor?.name}, ${read.data?.byteLength} bytes for ${width}x${height}) — cannot convert`);
        return null;
    }

    let bottomUp = read.bottomUp;
    const isRealWebGPU = read.isRealWebGPU;

    // Scene renders pass rows:"bottom-up" to force the flip — but a real WebGPU
    // backend already reads top-down, so forcing it there renders upside-down.
    if (options?.rows === "bottom-up" && !isRealWebGPU) bottomUp = true;
    if (bottomUp) {
        flipRowsInPlace(pixels, width, height);
    }
    if (options?.premultipliedAlpha) {
        for (let i = 0; i < pixels.length; i += 4) {
            const alpha = pixels[i + 3];
            if (alpha > 0 && alpha < 255) {
                for (let channel = 0; channel < 3; channel++) {
                    pixels[i + channel] = Math.min(255, Math.round(pixels[i + channel] * 255 / alpha));
                }
            }
        }
    }
    if (options?.encodeSRGB) {
        encodeLinearToSRGB(pixels);
    }
    options?.onTiming?.({ readbackMs: readFinished - started, conversionMs: performance.now() - readFinished });
    return pixels;
}

/**
 * The RAW readback, before any decode or reorientation — shared by the RGBA8 and
 * float readers so the heavily-tuned parts (texel-type matching, the WebGPU
 * alignment guard, the queue kick, row-order rules) exist once.
 *
 * `byteReuse`, when its size matches an 8-bit target, is read into DIRECTLY;
 * callers can then skip the copy entirely.
 */
async function readRenderTargetRaw(
    renderTarget: WebGLRenderTarget,
    renderer: WebGLRenderer,
    logger: ILogService | null,
    byteReuse?: Uint8Array,
): Promise<{ data: TypedArray; bottomUp: boolean; isRealWebGPU: boolean } | null> {
    const width = renderTarget.width;
    const height = renderTarget.height;
    const size = width * height * 4;

    // GL readbacks return rows bottom-up (framebuffer origin is bottom-left)
    // while canvas putImageData treats row 0 as the TOP row — those paths need
    // a vertical flip. The real WebGPU backend copies texture rows top-down, so it
    // must NOT be force-flipped (that renders previews upside-down).

    // WebGL Renderer has this method https://threejs.org/docs/#WebGLRenderer.readRenderTargetPixels
    if ("readRenderTargetPixels" in renderer) {
        // The read buffer must match the target's texel type — three silently
        // skips the read otherwise (postprocessing targets are HalfFloatType
        // by default, which used to come back all-black). Read raw, then
        // convert through the shared decode.
        const texelType = (renderTarget as { texture?: { type?: number } }).texture?.type;
        const data: TypedArray = texelType === HalfFloatType ? new Uint16Array(size)
            : texelType === FloatType ? new Float32Array(size)
                : (byteReuse && byteReuse.length === size ? byteReuse : new Uint8Array(size));
        // Classic WebGL's async API requires the caller's buffer; the newer
        // Renderer API below allocates its own. Avoid blocking the editing UI.
        if (typeof renderer.readRenderTargetPixelsAsync === "function") {
            await renderer.readRenderTargetPixelsAsync(renderTarget, 0, 0, width, height, data);
        } else {
            renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, data);
        }
        return { data, bottomUp: true, isRealWebGPU: false };
    }
    // in three 182 we have Renderer and only readRenderTargetPixelsAsync: https://threejs.org/docs/?q=readRenderTargetPixels#Renderer.readRenderTargetPixelsAsync
    else if ("readRenderTargetPixelsAsync" in renderer) {
        // GL-backed r182+ Renderer reads bottom-up like classic WebGL; only the
        // actual WebGPU backend copies top-down
        const backend = (renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend;
        // three allocates its staging buffer WITHOUT 4-byte alignment padding —
        // WebGPU rejects MapAsync on unaligned sizes ("Size must be a multiple
        // of 4"), the invalid buffer poisons the shared command encoder, and the
        // PAGE's own submits then fail with bindGroup/commandBuffer cascades
        // (seen live on the SSGI ballpool example: an odd-sized 2-byte-texel
        // target). Those formats aren't decodable to a preview anyway — skip
        // BEFORE three creates the doomed buffer.
        if (backend?.isWebGPUBackend === true && !isMapAsyncAligned(renderTarget)) {
            logger?.debug?.(`Readback: skipping ${width}x${height} target — texel layout not 4-byte aligned (three staging-buffer bug would poison the encoder)`);
            return null;
        }
        const isRealWebGPU = backend?.isWebGPUBackend === true;
        const readback = (renderer as any).readRenderTargetPixelsAsync(renderTarget, 0, 0, width, height) as Promise<TypedArray>;
        // WebGPU readbacks resolve on the GPU queue timeline. With on-demand
        // rendering nothing may tick the queue while idle, leaving the promise
        // pending until unrelated work submits a frame (symptom: thumbnail
        // spinner that only resolves on the next click). Nudge the queue with
        // an empty submit if it hasn't settled shortly; warn when it hangs.
        let settled = false;
        readback.catch(() => { /* handled by await below */ }).finally(() => { settled = true; });
        const kick = setTimeout(() => {
            if (settled) return;
            try {
                (renderer as unknown as { backend?: { device?: { queue?: { submit?: (buffers: unknown[]) => void } } } })
                    .backend?.device?.queue?.submit?.([]);
            } catch { /* WebGL2 backend: no device — nothing to kick */ }
        }, 250);
        const warn = setTimeout(() => {
            if (!settled) logger?.warn("Readback: GPU readback still pending after 2s — is anything submitting frames?");
        }, 2000);
        try {
            const arr = await readback;
            if (!arr) return null;
            return { data: arr, bottomUp: !isRealWebGPU, isRealWebGPU };
        } finally {
            clearTimeout(kick);
            clearTimeout(warn);
        }
    }
    return null;
}

/**
 * The same readback as {@link readbackRenderTargetToPixels}, but kept in LINEAR
 * FLOAT — values are NOT clamped to 0..1.
 *
 * This is what an HDR view needs. Quantising to 8 bits first destroys everything
 * above 1.0 (a sky texel of 4.08 becomes 255), so an exposure control applied
 * afterwards can only scale clipped white down uniformly instead of recovering
 * the highlights. Only useful against a float/half-float render target — an
 * 8-bit target has already thrown the range away before we get here.
 */
export async function readbackRenderTargetToFloats(
    renderTarget: WebGLRenderTarget,
    renderer: WebGLRenderer,
    logger: ILogService | null,
    options?: Pick<ReadbackOptions, "rows">,
    reuse?: Float32Array,
): Promise<Float32Array | null> {
    const width = renderTarget.width;
    const height = renderTarget.height;
    const size = width * height * 4;

    const read = await readRenderTargetRaw(renderTarget, renderer, logger);
    if (!read) return null;

    const floats = reuse && reuse.length === size ? reuse : new Float32Array(size);
    if (!copyReadbackFloats(read.data, width, height, floats)) {
        logger?.warn(`Readback: unexpected GPU readback buffer (${read.data?.constructor?.name}, ${read.data?.byteLength} bytes for ${width}x${height}) — cannot convert to float`);
        return null;
    }

    let bottomUp = read.bottomUp;
    if (options?.rows === "bottom-up" && !read.isRealWebGPU) bottomUp = true;
    if (bottomUp) flipFloatRowsInPlace(floats, width, height);
    return floats;
}

/**
 * Like {@link copyReadbackPixels}, but decodes into unclamped floats. Byte
 * sources are scaled 0..255 → 0..1 so a caller gets the same units either way
 * (they simply have no range above 1 to recover).
 */
export function copyReadbackFloats(arr: TypedArray | null | undefined, width: number, height: number, out: Float32Array): boolean {
    if (!arr || !arr.buffer) return false;
    const rowChannels = width * 4;

    if (arr instanceof Uint16Array) {
        const lut = _halfLut ??= buildHalfFloatLut();
        const stride = resolveRowStride(arr.length, height, rowChannels, 128);
        if (!stride) return false;
        for (let row = 0; row < height; row++) {
            const rowStart = row * stride;
            for (let i = 0; i < rowChannels; i++) out[row * rowChannels + i] = lut[arr[rowStart + i]];
        }
        return true;
    }

    if (arr instanceof Float32Array) {
        const stride = resolveRowStride(arr.length, height, rowChannels, 64);
        if (!stride) return false;
        for (let row = 0; row < height; row++) {
            const rowStart = row * stride;
            for (let i = 0; i < rowChannels; i++) out[row * rowChannels + i] = arr[rowStart + i];
        }
        return true;
    }

    if (arr.BYTES_PER_ELEMENT === 1 || arr instanceof Uint8Array || arr instanceof Uint8ClampedArray) {
        const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
        const stride = resolveRowStride(bytes.byteLength, height, rowChannels, 256);
        if (!stride) return false;
        for (let row = 0; row < height; row++) {
            const rowStart = row * stride;
            for (let i = 0; i < rowChannels; i++) out[row * rowChannels + i] = bytes[rowStart + i] / 255;
        }
        return true;
    }

    return false;
}

/** Row-order reversal for tightly packed RGBA float data. Exported for tests. */
export function flipFloatRowsInPlace(data: Float32Array, width: number, height: number): void {
    const rowUnits = width * 4;
    const scratch = new Float32Array(rowUnits);
    for (let top = 0, bottom = height - 1; top < bottom; top++, bottom--) {
        const topRow = data.subarray(top * rowUnits, top * rowUnits + rowUnits);
        const bottomRow = data.subarray(bottom * rowUnits, bottom * rowUnits + rowUnits);
        scratch.set(topRow);
        topRow.set(bottomRow);
        bottomRow.set(scratch);
    }
}

/**
 * Copies a GPU readback buffer into a tightly packed RGBA8 array. Exported for
 * tests. Handles the shapes real pages produce:
 * - exact-size byte buffers (WebGL, fixed three versions),
 * - 256-byte-row-padded byte buffers (WebGPU `copyTextureToBuffer` row
 *   alignment — some three versions return the padded buffer verbatim, which
 *   made the naive `set()` throw "offset is out of bounds"),
 * - float buffers (exact or row-padded), converted 0..1 → 0..255,
 * - half-float buffers (Uint16Array — the DEFAULT postprocessing target type,
 *   HalfFloatType), decoded via LUT and converted 0..1 → 0..255.
 * Returns false for shapes it cannot interpret — callers treat that as
 * "no preview" instead of throwing.
 */
export function copyReadbackPixels(arr: TypedArray | null | undefined, width: number, height: number, out: Uint8Array): boolean {
    if (!arr || !arr.buffer) return false;

    // half-float source (Uint16Array): decode f16 per channel. A Uint16Array
    // from a render-target readback is half-float in practice — three
    // allocates it for HalfFloatType, the default for postprocessing chains.
    if (arr instanceof Uint16Array) {
        const lut = _halfLut ??= buildHalfFloatLut();
        // 256-byte alignment = 128 u16 channels
        const resolved = resolveChannels(arr.length, width, height, 128);
        if (!resolved) return false;
        const { channels, stride } = resolved;
        if (channels < 4) {
            for (let row = 0; row < height; row++) {
                const rowStart = row * stride;
                for (let x = 0; x < width; x++) {
                    const src = rowStart + x * channels;
                    writeNarrowTexel(out, (row * width + x) * 4, channels,
                        (c) => Math.round(Math.min(1, Math.max(0, lut[arr[src + c]])) * 255));
                }
            }
            return true;
        }
        const rowChannels = width * 4;
        for (let row = 0; row < height; row++) {
            const rowStart = row * stride;
            for (let i = 0; i < rowChannels; i++) {
                out[row * rowChannels + i] = Math.round(Math.min(1, Math.max(0, lut[arr[rowStart + i]])) * 255);
            }
        }
        return true;
    }

    // float source (e.g. float render targets): convert per channel
    if (arr instanceof Float32Array) {
        // 256-byte alignment = 64 float channels
        const resolved = resolveChannels(arr.length, width, height, 64);
        if (!resolved) return false;
        const { channels, stride } = resolved;
        if (channels < 4) {
            for (let row = 0; row < height; row++) {
                const rowStart = row * stride;
                for (let x = 0; x < width; x++) {
                    const src = rowStart + x * channels;
                    writeNarrowTexel(out, (row * width + x) * 4, channels,
                        (c) => Math.round(Math.min(1, Math.max(0, arr[src + c])) * 255));
                }
            }
            return true;
        }
        const rowChannels = width * 4;
        for (let row = 0; row < height; row++) {
            const rowStart = row * stride;
            for (let i = 0; i < rowChannels; i++) {
                out[row * rowChannels + i] = Math.round(Math.min(1, Math.max(0, arr[rowStart + i])) * 255);
            }
        }
        return true;
    }

    // byte source — exact or row-padded
    if (arr.BYTES_PER_ELEMENT === 1 || arr instanceof Uint8Array || arr instanceof Uint8ClampedArray) {
        const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
        const resolved = resolveChannels(bytes.byteLength, width, height, 256);
        if (!resolved) return false;
        const { channels, stride } = resolved;
        if (channels < 4) {
            for (let row = 0; row < height; row++) {
                const rowStart = row * stride;
                for (let x = 0; x < width; x++) {
                    const src = rowStart + x * channels;
                    writeNarrowTexel(out, (row * width + x) * 4, channels, (c) => bytes[src + c]);
                }
            }
            return true;
        }
        const rowBytes = width * 4;
        if (stride === rowBytes) {
            out.set(bytes.subarray(0, rowBytes * height));
            return true;
        }
        for (let row = 0; row < height; row++) {
            out.set(bytes.subarray(row * stride, row * stride + rowBytes), row * rowBytes);
        }
        return true;
    }

    // exotic formats (e.g. packed depth): no decode — treat as unreadable
    return false;
}

// f16 → f32 decode table (65536 entries, ~256KB, built on first half-float
// readback). Indexing a LUT beats per-texel bit math for full-frame decodes.
let _halfLut: Float32Array | null = null;
function buildHalfFloatLut(): Float32Array {
    const lut = new Float32Array(65536);
    for (let bits = 0; bits < 65536; bits++) {
        const sign = bits & 0x8000 ? -1 : 1;
        const exponent = (bits >> 10) & 0x1f;
        const fraction = bits & 0x03ff;
        lut[bits] =
            exponent === 0 ? sign * fraction * 2 ** -24 // subnormal
                : exponent === 31 ? (fraction ? NaN : sign * Infinity)
                    : sign * (1024 + fraction) * 2 ** (exponent - 25);
    }
    return lut;
}

/**
 * Infers the row stride (in array units) of a readback buffer:
 * - tightly packed (`rowUnits * height`),
 * - rows padded to the WebGPU 256-byte alignment — INCLUDING the minimal-copy
 *   layout where the LAST row is unpadded (`aligned * (height-1) + rowUnits`,
 *   e.g. 45928 bytes for 90x90: 512*89 + 360),
 * - any other uniform stride that divides the total.
 * Returns 0 when no consistent stride fits.
 */
/**
 * How many channels per texel a readback buffer holds, plus its row stride.
 *
 * A render target is not always RGBA: SSGI's AO buffer is RedFormat, and
 * depth/mask/velocity passes are commonly R or RG. The buffer carries no format
 * of its own, so the count is inferred from its size — WIDEST FIRST, so an
 * ordinary (possibly row-padded) RGBA readback can never be mistaken for a
 * narrower one.
 */
function resolveChannels(
    totalUnits: number,
    width: number,
    height: number,
    alignUnits: number,
): { channels: number; stride: number } | null {
    for (const channels of [4, 2, 1]) {
        const stride = resolveRowStride(totalUnits, height, width * channels, alignUnits);
        if (stride) return { channels, stride };
    }
    return null;
}

/**
 * Writes one texel of a NARROW (1- or 2-channel) source into RGBA8.
 *
 * A single channel is replicated into RGB so an AO or mask buffer reads as an
 * image rather than a red wash; two channels keep R/G and leave blue empty.
 * Alpha is forced opaque — these formats carry none, and respecting the absent
 * alpha would render every such preview as an empty checkerboard.
 */
function writeNarrowTexel(out: Uint8Array, at: number, channels: number, sample: (channel: number) => number): void {
    const r = sample(0);
    if (channels === 1) {
        out[at] = out[at + 1] = out[at + 2] = r;
        out[at + 3] = 255;
        return;
    }
    out[at] = r;
    out[at + 1] = sample(1);
    out[at + 2] = 0;
    out[at + 3] = 255;
}

function resolveRowStride(totalUnits: number, height: number, rowUnits: number, alignUnits: number): number {
    if (height <= 0 || rowUnits <= 0) return 0;
    if (totalUnits === rowUnits * height) return rowUnits;
    const aligned = Math.ceil(rowUnits / alignUnits) * alignUnits;
    if (totalUnits === aligned * height) return aligned;
    if (totalUnits === aligned * (height - 1) + rowUnits) return aligned;
    if (totalUnits > rowUnits * height && totalUnits % height === 0 && totalUnits / height >= rowUnits) {
        return totalUnits / height;
    }
    return 0;
}

/** Reverses the row order of tightly packed RGBA8 pixels in place. Exported for tests. */
export function flipRowsInPlace(pixels: Uint8Array, width: number, height: number): void {
    const rowBytes = width * 4;
    const scratch = new Uint8Array(rowBytes);
    for (let top = 0, bottom = height - 1; top < bottom; top++, bottom--) {
        const topRow = pixels.subarray(top * rowBytes, top * rowBytes + rowBytes);
        const bottomRow = pixels.subarray(bottom * rowBytes, bottom * rowBytes + rowBytes);
        scratch.set(topRow);
        topRow.set(bottomRow);
        bottomRow.set(scratch);
    }
}

/** In-place linear→sRGB transfer on RGBA8 pixel data (alpha untouched).
 *  Exported so consumers that read RAW (linear) pixels for CPU post-processing
 *  — channel isolation, exposure — can apply the display encode afterwards. */
export function encodeLinearToSRGB(pixels: Uint8Array | Uint8ClampedArray): void {
    // 256-entry LUT — the transfer only depends on the 8-bit channel value
    const lut = _srgbLut ??= buildSRGBLut();
    for (let i = 0; i < pixels.length; i += 4) {
        pixels[i] = lut[pixels[i]];
        pixels[i + 1] = lut[pixels[i + 1]];
        pixels[i + 2] = lut[pixels[i + 2]];
    }
}
let _srgbLut: Uint8Array | null = null;
function buildSRGBLut(): Uint8Array {
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
        const c = i / 255;
        const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
        lut[i] = Math.round(Math.min(1, Math.max(0, s)) * 255);
    }
    return lut;
}

/**
 * Whether `width × height × texelBytes` is 4-byte aligned — the WebGPU
 * MapAsync requirement three's readback staging buffer ignores. RGBA8 and
 * RGBA16F/32F are always aligned; 2-byte single-channel targets (R16F, depth
 * variants) with an odd pixel count are the ones that break.
 */
function isMapAsyncAligned(renderTarget: WebGLRenderTarget): boolean {
    const texture = (renderTarget as { texture?: { type?: number; format?: number } }).texture;
    if (!texture) return true; // unknown shape — let three try
    // three constants: types UnsignedByte 1009 (1B), UnsignedShort 1012 /
    // HalfFloat 1016 (2B), UnsignedInt 1014 / Float 1015 (4B); formats
    // Red(Integer) 1028/1029 (1ch), RG(Integer) 1030/1031 (2ch), Depth 1026 /
    // DepthStencil 1027 (1ch), RGBA(Integer) 1023/1033 and default (4ch).
    const type = texture.type ?? 1009;
    const bytesPerChannel = type === 1012 || type === 1016 ? 2 : type === 1014 || type === 1015 ? 4 : 1;
    const format = texture.format ?? 1023;
    const channels = format === 1028 || format === 1029 || format === 1026 || format === 1027 ? 1
        : format === 1030 || format === 1031 ? 2
            : 4;
    return (renderTarget.width * renderTarget.height * bytesPerChannel * channels) % 4 === 0;
}

/** In-place sRGB→linear transfer (alpha untouched) — the inverse, for CPU
 *  post-processing (exposure) that must run in LINEAR space on pixels that
 *  are already display-encoded (stored-encoded image textures). */
export function decodeSRGBToLinear(pixels: Uint8Array | Uint8ClampedArray): void {
    const lut = _srgbInverseLut ??= buildSRGBInverseLut();
    for (let i = 0; i < pixels.length; i += 4) {
        pixels[i] = lut[pixels[i]];
        pixels[i + 1] = lut[pixels[i + 1]];
        pixels[i + 2] = lut[pixels[i + 2]];
    }
}
let _srgbInverseLut: Uint8Array | null = null;
function buildSRGBInverseLut(): Uint8Array {
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
        const s = i / 255;
        const c = s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
        lut[i] = Math.round(Math.min(1, Math.max(0, c)) * 255);
    }
    return lut;
}
